// ── Quest cluster ──
// Extracted verbatim from MapCanvas.tsx as TWO hooks, deliberately not merged:
// in MapCanvas they sit at very different points in the effect order (the
// prefetch runs early, the marker build runs after the scene controllers are
// constructed), and that order is load-bearing. Each is called from its
// original position, so the sequence is unchanged.
//
// Bodies are byte-identical to the originals; closure dependencies are
// parameters under their ORIGINAL names. `hideQuestMarker` arrives as a thunk
// because in MapCanvas it is a `const` arrow declared BELOW the call site.
import { useEffect, type MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import type { MapObject } from '../../models/mapObject';
import type { DailyQuestAssignAndGetResponse, QuestObjective } from '../../models/apiTypes';
import type { MapModalName, MapModalPayload } from '../../stores/useMapModalStore';
import type { HighlightManager } from './controller/HighlightManager';
import type { MarkerFactory } from './controller/markers/MarkerFactory';
import { mapSceneSingleton } from './mapScene';
import { prefetchAllQuests } from '../../utils/quest/questsCache';
import type { useCreateUserAction } from '../../hooks/useCreateUserAction';

export interface UseQuestsPrefetchParams {
  /** Query results — by value, re-read every render (also the effect deps). */
  dailyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  hourlyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  marketingQuestsData: DailyQuestAssignAndGetResponse | undefined;
  queryClient: QueryClient;
}

/** Prefetches the three quest lists and derives the top-bar "unfinished" badge. */
export function useQuestsPrefetch({
  dailyQuestsData,
  hourlyQuestsData,
  marketingQuestsData,
  queryClient,
}: UseQuestsPrefetchParams): { hasUnfinishedQuests: boolean } {
  useEffect(() => {
    if (dailyQuestsData && hourlyQuestsData && marketingQuestsData) {
      return;
    }

    prefetchAllQuests(queryClient).catch((error) => {
      console.error("[MapCanvas] Failed to load quests:", error);
    });
  }, [dailyQuestsData, hourlyQuestsData, marketingQuestsData, queryClient]);

  const allQuests = [
    ...(dailyQuestsData?.quests || []),
    ...(hourlyQuestsData?.quests || []),
    ...(marketingQuestsData?.quests || [])
  ];

  const hasUnfinishedQuests = allQuests.some(quest => 
    !quest.is_finished || (quest.is_finished && !quest.reward_claimed)
  );

  return { hasUnfinishedQuests };
}

export interface UseQuestMarkersParams {
  /** Effect deps / gating flags — by value. */
  markersReady: boolean;
  isPixiAppReady: boolean;
  dailyQuestsUpdatedAt: number | undefined;
  dailyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  /** Facades — by reference; `.current` is read after `await`. */
  markerFactoryRef: MutableRefObject<MarkerFactory | null>;
  highlightManagerRef: MutableRefObject<HighlightManager | null>;
  getLatestMapObject: (mapObject: MapObject | null | undefined) => MapObject | null;
  /** Thunk — declared below the call site in MapCanvas. */
  hideQuestMarker: (mapObject: MapObject) => void;
  setSelectedMapObject: (obj: MapObject | null) => void;
  openModal: (name: MapModalName, payload?: Partial<MapModalPayload>) => void;
  createUserAction: ReturnType<typeof useCreateUserAction>['createUserAction'];
}

/** Builds the floating quest markers above city hall / shop / hall of fame. */
export function useQuestMarkers({
  markersReady,
  isPixiAppReady,
  dailyQuestsUpdatedAt,
  dailyQuestsData,
  sceneRef,
  markerFactoryRef,
  highlightManagerRef,
  getLatestMapObject,
  hideQuestMarker,
  setSelectedMapObject,
  openModal,
  createUserAction,
}: UseQuestMarkersParams): void {
  // ── Quest markers (separate from main markers to avoid full rebuild on quest changes) ──
  useEffect(() => {
    if (!markersReady || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app || !isPixiAppReady) return;
    if (!dailyQuestsData?.quests) return;

    const mapObjectsLayer = sceneRef.current.layers.mapObjects;
    const mapObjectMarkers = sceneRef.current.markers.mapObjects;
    const questMarkers = sceneRef.current.markers.quests;

    // Clean old quest markers
    questMarkers.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    questMarkers.clear();

    const hasUnfinished = (targetAction: string) =>
      dailyQuestsData.quests.some(quest => {
        if (quest.is_finished) return false;
        return quest.objectives?.some((obj: QuestObjective) =>
          obj.type === 'visit_static_map_object' &&
          obj.target_action === targetAction &&
          (!obj.is_completed || obj.current_count < obj.target_count)
        ) ?? false;
      });

    const currentMO = sceneRef.current.data.mapObjects;
    const cityhallsList = currentMO.filter(obj => obj.type === 'cityhall');
    const shopsList = currentMO.filter(obj => obj.type === 'shop');
    const hofList = currentMO.filter(obj => obj.type === 'hell_of_flame');

    const objectsWithQuestMarkers: Array<{ obj: MapObject; imagePath: string }> = [];
    if (hasUnfinished('visit_city_hall')) {
      objectsWithQuestMarkers.push(...cityhallsList.map(obj => ({ obj, imagePath: '/ui/world-map/quest-main-marker-completed.png' })));
    }
    if (hasUnfinished('visit_shop')) {
      objectsWithQuestMarkers.push(...shopsList.map(obj => ({ obj, imagePath: '/ui/world-map/quest-main-marker-completed.png' })));
    }
    if (hasUnfinished('visit_hell_of_flame')) {
      objectsWithQuestMarkers.push(...hofList.map(obj => ({ obj, imagePath: '/ui/world-map/quest-main-marker-completed.png' })));
    }

    if (objectsWithQuestMarkers.length === 0) return;

    let cancelled = false;

    (async () => {
      for (const { obj, imagePath } of objectsWithQuestMarkers) {
        if (cancelled || !sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) break;

        const mainMarker = mapObjectMarkers.get(obj.id);
        if (!mainMarker) continue;

        try {
          const questMarker = await markerFactoryRef.current!.createQuestMarker(obj, mainMarker, imagePath);
          if (!questMarker || cancelled) continue;

          const storedMainMarker = mainMarker;
          questMarker.on('pointertap', (event: PIXI.FederatedPointerEvent) => {
            event.stopPropagation();
            if (sceneRef.current.flags.godModeActive) return;
            if (!sceneRef.current.pixi.viewport) return;

            if (sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
              sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
              sceneRef.current.sprites.mapObjectHighlight.destroy();
              sceneRef.current.sprites.mapObjectHighlight = null;
              mapSceneSingleton.mapObjectHighlight = null;
            }

            const latestMapObject = getLatestMapObject(obj);
            if (latestMapObject) highlightManagerRef.current!.apply(latestMapObject, storedMainMarker);

            hideQuestMarker(latestMapObject ?? obj);
            setSelectedMapObject(latestMapObject ?? obj);
            setTimeout(() => {
              const finalMapObject = latestMapObject ?? obj;
              if (!finalMapObject) return;
              if (finalMapObject.type === 'cityhall') {
                openModal('mapObject');
                createUserAction('visit_city_hall').catch(console.error);
              } else if (finalMapObject.type === 'shop') {
                openModal('navConfirm', { navConfirmDestination: 'shop' });
              } else if (finalMapObject.type === 'hell_of_flame') {
                openModal('navConfirm', { navConfirmDestination: 'hall_of_fame' });
              }
            }, 100);
          });

          questMarker.visible = sceneRef.current.flags.showPlots;
          mapObjectsLayer.addChild(questMarker);
          questMarkers.set(obj.id, questMarker);
        } catch (error) {
          console.error(`[QuestMarker] Error creating quest marker for ${obj.type} id ${obj.id}:`, error);
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- marker rebuild triggers only; omit mapObjects thrash
  }, [markersReady, isPixiAppReady, dailyQuestsUpdatedAt]);
}
